<template>
  <Card v-if="!shouldContinue">
    <template #title>
      Reserveren
    </template>
    <template #content>
      <InlineMessage v-if="noLongerAvailable" class="p-mt-2 p-mb-2" severity="warn">
        De gekozen combinatie van datum, tijd en locatie is helaas niet meer beschikbaar. Probeer het nog eens.
      </InlineMessage>
      <ProgressBar v-if="!availability" mode="indeterminate"/>
      <div v-if="availability" class="p-fluid">
        <div class="p-field">
          <label for="date">Datum</label>
          <Calendar
              id="date"
              v-model="reservation.date"
              @date-select="onChangeReservation()"
              :touchUI="true"
              :manualInput="false"
              :selectOtherMonths="true"
              :minDate="getMinDate()"
              :maxDate="getMaxDate()"
              :disabledDates="getDisabledDates()"
              placeholder="Kies een datum"
          />
          <InlineMessage v-if="reservation.date !== null && !isDateAvailable()" class="p-mt-2 p-mb-2" severity="warn">
            Helaas zijn er voor deze dag geen reserveringen meer mogelijk.
          </InlineMessage>
        </div>
        <div class="p-field" v-if="isDateAvailable()">
          <label for="numberOfPeople">Aantal</label>
          <Dropdown
              id="numberOfPeople"
              v-model="reservation.numberOfPeople"
              @change="onChangeReservation()"
              :options="chooseNumberOfPeople"
              optionLabel="name"
              placeholder="Kies aantal personen"
          />
          <InlineMessage v-if="reservation.numberOfPeople !== null && !isTimeAvailable()" class="p-mt-2 p-mb-2"
                         severity="warn">
            Sorry, een tafel voor {{ reservation.numberOfPeople.code }}
            {{ reservation.numberOfPeople.code === 1 ? 'persoon' : 'personen' }} is niet meer beschikbaar op de
            geselecteerde datum
          </InlineMessage>
        </div>
        <div class="p-field" v-if="isTimeAvailable()">
          <label for="time">Beschikbare tijden:</label>
          <Dropdown
              id="time"
              v-model="reservation.time"
              @change="onChangeReservation()"
              :options="getAvailableTimes()"
              optionLabel="name"
              placeholder="Kies een tijd"
          />
        </div>
        <div class="p-field" v-if="isDateAvailable() && isTimeAvailable() && reservation.time !== null">
          <label for="time">Locatie:</label>
          <SelectButton v-model="reservation.location" :options="getAvailableLocations()" optionLabel="name"/>
        </div>
      </div>
    </template>
    <template v-if="isValidReservation()" #footer>
      <Button @click="shouldContinue = true" icon="pi pi-check" label="Doorgaan"/>
    </template>
  </Card>

  <Card v-if="availability && shouldContinue && !result" class="shouldContinue">
    <template #title>
      Bevestig je reservering
    </template>
    <template #content>
      <div v-if="submitting" class="loader-overlay">
        <i class="pi pi-spin pi-spinner" style="fontSize: 2rem"></i>
      </div>
      <DataTable :value="getOverview()">
        <Column field="date" header="Datum"></Column>
        <Column field="time" header="Tijd"></Column>
        <Column field="numberOfPeople" header="Aantal"></Column>
        <Column field="location" header="Locatie"></Column>
        <template #footer>
          <div>
            <Button icon="pi pi-angle-double-left" label="Aanpassen" @click="shouldContinue = false"/>
          </div>
        </template>
      </DataTable>

      <form @submit.prevent="handleSubmit(!v$.$invalid)" class="p-fluid p-mt-5">
        <div class="p-field">
          <label for="name" :class="{'p-error':v$.name.$invalid && submitted}">Naam*</label>
          <InputText id="name" type="text" v-model="v$.name.$model"
                     :class="{'p-invalid':v$.name.$invalid && submitted}" placeholder="Uw naam"/>
          <small v-if="(v$.name.$invalid && submitted) || v$.name.$pending.$response" class="p-error">
            Naam is verplicht
          </small>
        </div>
        <div class="p-field">
          <label for="email" :class="{'p-error':v$.email.$invalid && submitted}">E-mail*</label>
          <InputText id="email" type="text" v-model="v$.email.$model"
                     :class="{'p-invalid':v$.email.$invalid && submitted}" placeholder="naam@email.nl"/>
          <span v-if="v$.email.$error && submitted">
              <span id="email-error" v-for="(error, index) of v$.email.$errors" :key="index">
              <small class="p-error">{{
                  error.$message.replace('Value is required', 'Email is verplicht').replace('Value is not a valid email address', 'Vul een geldig e-mail in')
                }}</small>
              </span>
          </span>
          <small v-else-if="(v$.email.$invalid && submitted) || v$.email.$pending.$response" class="p-error">
            {{ v$.email.required.$message.replace('Value', 'Email') }}
          </small>
        </div>
        <div class="p-field">
          <label for="phoneNumber" :class="{'p-error':v$.phoneNumber.$invalid && submitted}">Telefoonnummer*</label>
          <InputText id="phoneNumber" type="text" v-model="v$.phoneNumber.$model"
                     :class="{'p-invalid':v$.phoneNumber.$invalid && submitted}" placeholder="0612345678"/>
          <small v-if="(v$.phoneNumber.$invalid && submitted) || v$.phoneNumber.$pending.$response" class="p-error">
            Telefoonnummer is verplicht
          </small>
        </div>
        <div class="p-field">
          <label for="info">Info</label>
          <Textarea id="info" v-model="info" :autoResize="true" rows="5" cols="30"/>
        </div>
        <InlineMessage v-if="error" class="p-mt-2 p-mb-2" severity="error">
          Sorry, er is aan onze kant wat mis gegaan. Probeer het nog eens, of neem contact op via:
          <a href="tel:036-5369959">036-5369959</a>
        </InlineMessage>
        <Button type="submit" label="Bevestigen"/>
      </form>
    </template>
  </Card>

  <Card v-if="result">
    <template #title>
      Jouw reservering
    </template>
    <template #content>
      <InlineMessage class="p-mt-2 p-mb-2" severity="success">
        Bedankt, we hebben je reservering ontvangen!
      </InlineMessage>
      <DataTable :value="getOverview()">
        <Column field="date" header="Datum"></Column>
        <Column field="time" header="Tijd"></Column>
        <Column field="numberOfPeople" header="Aantal"></Column>
        <Column field="location" header="Locatie"></Column>
      </DataTable>

      <DataTable :value="getDetails()">
        <Column field="name" header="Naam"></Column>
        <Column field="email" header="E-mail"></Column>
        <Column field="phoneNumber" header="Telefoonnummer"></Column>
        <Column field="info" header="Info"></Column>
      </DataTable>
    </template>
  </Card>
</template>

<script>
const axios = require('axios')
import {email, required} from '@vuelidate/validators'
import {useVuelidate} from '@vuelidate/core'

import ProgressBar from 'primevue/progressbar'
import Card from 'primevue/card'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import InlineMessage from 'primevue/inlinemessage'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import SelectButton from 'primevue/selectbutton'
import Textarea from 'primevue/textarea'

const defaultReservation = {
  date: null,
  numberOfPeople: null,
  time: null,
  location: null
}

export default {
  name: 'App',
  setup: () => ({v$: useVuelidate()}),
  components: {
    ProgressBar,
    Card,
    Calendar,
    Dropdown,
    Button,
    InlineMessage,
    DataTable,
    Column,
    InputText,
    SelectButton,
    Textarea
  },
  data() {
    return {
      availability: null,
      reservation: Object.assign({}, defaultReservation),
      name: null,
      email: null,
      phoneNumber: null,
      info: null,
      submitted: false,
      submitting: false,
      error: false,
      noLongerAvailable: false,
      result: false,

      shouldContinue: false,

      chooseNumberOfPeople: []
    }
  },
  created() {
    this.getAvailability()
  },
  validations() {
    return {
      name: {
        required
      },
      email: {
        required,
        email
      },
      phoneNumber: {
        required
      }
    }
  },
  methods: {
    getAvailability() {
      axios.get(`${process.env.VUE_APP_BASE_URL}/availability`)
          .then((result) => {
            this.availability = result.data.availability
            for (let i = 1; i <= result.data.maxNumberOfPeople; i++) {
              this.chooseNumberOfPeople.push({name: `${i} ${i === 1 ? 'persoon' : 'personen'}`, code: i})
            }
          })
    },
    onChangeReservation() {
      this.noLongerAvailable = false

      const availableLocations = this.getAvailableLocations()
      if (this.reservation.location !== null) {
        if (availableLocations.findIndex(location => location.code === this.reservation.location.code) === -1) {
          if (availableLocations.length === 1) {
            this.reservation.location = availableLocations[0]
          } else {
            this.reservation.location = null
          }
        }
      } else {
        if (availableLocations.length === 1) {
          this.reservation.location = availableLocations[0]
        }
      }

      if (this.reservation.time !== null) {
        if (this.getAvailableTimes().findIndex(time => time.code === this.reservation.time.code) === -1) {
          this.reservation.time = null
        }
      }
    },
    getDateFromJsDate(date) {
      const year = date.getFullYear()
      const month = ('0' + (date.getMonth() + 1)).slice(-2)
      const day = ('0' + date.getDate()).slice(-2)

      return `${year}-${month}-${day}`
    },
    getMinDate() {
      if (!this.availability) {
        return null
      }

      return new Date(Object.keys(this.availability)[0])
    },
    getMaxDate() {
      if (!this.availability) {
        return null
      }

      const dates = Object.keys(this.availability)

      return new Date(dates[dates.length - 1])
    },
    getDisabledDates() {
      if (!this.availability) {
        return null
      }

      const disabledDates = []

      const dates = Object.keys(this.availability)

      const date = new Date(this.getMinDate().getTime())
      const maxDate = new Date(this.getMaxDate().getTime())

      while (date <= maxDate) {
        if (!dates.includes(this.getDateFromJsDate(date))) {
          disabledDates.push(new Date(date.getTime()))
        }

        date.setDate(date.getDate() + 1)
      }

      return disabledDates
    },
    isValidReservation() {
      for (let item in this.reservation) {
        if (this.reservation[item] === null) {
          return false
        }
      }

      return true
    },
    isDateAvailable() {
      if (this.reservation.date === null) {
        return false
      }

      const date = this.getDateFromJsDate(this.reservation.date)

      return date in this.availability
    },
    isTimeAvailable() {
      if (this.reservation.date === null || this.reservation.numberOfPeople === null) {
        return false
      }

      const availableTimes = this.availability[this.getDateFromJsDate(this.reservation.date)]

      for (let time in availableTimes) {
        for (let location in availableTimes[time]) {
          if (this.reservation.numberOfPeople.code >= availableTimes[time][location].min && this.reservation.numberOfPeople.code <= availableTimes[time][location].max) {
            return true
          }
        }
      }

      return false
    },
    getAvailableTimes() {
      if (!this.isDateAvailable() || !this.isTimeAvailable()) {
        return []
      }

      const readableDate = this.getDateFromJsDate(this.reservation.date)
      const availableTimesOnDate = this.availability[readableDate]

      const availableTimes = []

      for (let time in availableTimesOnDate) {
        for (let location in availableTimesOnDate[time]) {
          if (this.reservation.numberOfPeople.code >= availableTimesOnDate[time][location].min && this.reservation.numberOfPeople.code <= availableTimesOnDate[time][location].max) {
            if (!availableTimes.includes(time)) {
              availableTimes.push(time)
            }
          }
        }
      }

      const primeTimes = []

      availableTimes.forEach(time => {
        primeTimes.push({name: time, code: time})
      })

      return primeTimes
    },
    getAvailableLocations() {
      if (!this.isDateAvailable() || !this.isTimeAvailable() || this.reservation.time === null) {
        return []
      }

      const readableDate = this.getDateFromJsDate(this.reservation.date)
      const locations = this.availability[readableDate][this.reservation.time.code]

      const availableLocations = []

      for (let location in locations) {
        if (this.reservation.numberOfPeople.code >= locations[location].min && this.reservation.numberOfPeople.code <= locations[location].max) {
          availableLocations.push({name: location, code: location})
        }
      }

      return availableLocations
    },
    getOverview() {
      const date = `${this.reservation.date.getDate()}-${this.reservation.date.getMonth() + 1}-${this.reservation.date.getFullYear()}`

      return [
        {
          date,
          time: this.reservation.time.name,
          numberOfPeople: this.reservation.numberOfPeople.name,
          location: this.reservation.location.name
        }
      ]
    },
    getDetails() {
      return [
        {
          name: this.name,
          email: this.email,
          phoneNumber: this.phoneNumber,
          info: this.info
        }
      ]
    },
    handleSubmit(isFormValid) {
      this.submitted = true
      this.error = false

      if (!isFormValid) {
        return
      }

      this.submitting = true

      const data = {
        reservation: this.reservation,
        name: this.name,
        email: this.email,
        phoneNumber: this.phoneNumber,
        info: this.info,
      }

      axios.post(`${process.env.VUE_APP_BASE_URL}/reserve`, data)
          .then((data) => {
            this.result = true
            this.submitting = false
            console.log('then', data)
          })
          .catch((error) => {
            if (error.response.status === 422) {
              this.noLongerAvailable = true
              this.reservation = Object.assign({}, defaultReservation)
              this.availability = null
              this.submitting = false
              this.shouldContinue = false
              this.getAvailability()
            } else {
              this.error = true
              this.submitting = false
            }
          })
    }
  }
}
</script>

<style lang="scss">
.p-datepicker table {
  th {
    // needed to overwrite wordpress th align left
    text-align: center;
  }
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;

  .shouldContinue {
    position: relative;
  }

  .loader-overlay {
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: rgba(76, 175, 80, 0.3);

    .pi-spinner {
      flex: 0 0 0;
    }
  }
}
</style>
